import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Download, Smartphone, Shield, ChevronRight, FileDown, CheckCircle, AlertTriangle, Info, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DownloadPage = () => {
  const [apks, setApks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buildVersion, setBuildVersion] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/download/apk`)
      .then(r => r.json())
      .then(data => setApks(data.apks || []))
      .catch(() => setApks([]))
      .finally(() => setLoading(false));
    fetch(`${API_URL}/api/download/version`)
      .then(r => r.json())
      .then(data => setBuildVersion(data.version_name || null))
      .catch(() => {});
  }, []);

  const productionApks = apks.filter(a => a.filename.includes('release-production'));
  const otherApks = apks.filter(a => !a.filename.includes('release-production'));

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('id-ID', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const versionFromApk = productionApks.length > 0
    ? productionApks[0].filename.match(/v([\d.]+)/)?.[1]
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-900" data-testid="download-page">
      {/* Header */}
      <div className="max-w-2xl mx-auto px-4 pt-12 pb-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/20 mb-6">
          <Smartphone className="w-8 h-8 text-amber-400" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">SIGAP GURU</h1>
        <p className="text-indigo-300 text-lg">Download Aplikasi Android</p>
        <p className="text-indigo-400 text-sm mt-1">Sistem Informasi Kehadiran & Penilaian — Kabupaten Asmat</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 pb-16 space-y-6">
        {/* Production APK (Primary) */}
        {loading ? (
          <Card className="border-indigo-700 bg-indigo-900/50 backdrop-blur">
            <CardContent className="py-12 text-center">
              <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-indigo-300">Memuat daftar APK...</p>
            </CardContent>
          </Card>
        ) : productionApks.length > 0 ? (
          <>
            {productionApks.map(apk => (
              <Card key={apk.filename} className="border-amber-500/30 bg-indigo-900/60 backdrop-blur overflow-hidden" data-testid="production-apk-card">
                <div className="h-1 bg-gradient-to-r from-amber-400 to-amber-600" />
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2 text-lg">
                        <Shield className="w-5 h-5 text-amber-400" />
                        APK Produksi
                        <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs">Recommended</Badge>
                      </CardTitle>
                      <CardDescription className="text-indigo-300 mt-1">
                        Siap untuk pengujian dan distribusi
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-indigo-800/40 rounded-lg p-3">
                      <p className="text-indigo-400 text-xs mb-1">Ukuran</p>
                      <p className="text-white font-semibold">{apk.size_mb} MB</p>
                    </div>
                    <div className="bg-indigo-800/40 rounded-lg p-3">
                      <p className="text-indigo-400 text-xs mb-1">Diperbarui</p>
                      <p className="text-white font-semibold text-xs">{formatDate(apk.modified)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" /> Signed
                    </Badge>
                    <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-xs">
                      <Shield className="w-3 h-3 mr-1" /> SSL Pinning
                    </Badge>
                    <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 text-xs">
                      Anti-Mock GPS
                    </Badge>
                  </div>
                  <a
                    href={`${API_URL}/api/download/apk/${apk.filename}`}
                    download
                    className="block w-full"
                    data-testid="download-production-btn"
                  >
                    <div className="w-full bg-amber-500 hover:bg-amber-600 text-indigo-950 font-semibold h-12 text-base rounded-md flex items-center justify-center gap-2 transition-colors cursor-pointer">
                      <Download className="w-5 h-5" />
                      Download APK Produksi ({apk.size_mb} MB)
                    </div>
                  </a>
                </CardContent>
              </Card>
            ))}
          </>
        ) : null}

        {/* QR Code */}
        {productionApks.length > 0 && (
          <Card className="border-indigo-700/50 bg-indigo-900/40 backdrop-blur" data-testid="qr-code-card">
            <CardContent className="py-6">
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center gap-2 text-indigo-200">
                  <QrCode className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium">Scan untuk download di HP</span>
                </div>
                <div className="bg-white p-3 rounded-xl">
                  <QRCodeSVG
                    value={`${API_URL}/api/download/apk/${productionApks[0].filename}`}
                    size={160}
                    bgColor="#ffffff"
                    fgColor="#1e1b4b"
                    level="M"
                  />
                </div>
                <p className="text-indigo-400 text-xs text-center max-w-xs">
                  Arahkan kamera HP ke QR code ini untuk langsung download APK produksi
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Other APKs */}
        {otherApks.length > 0 && (
          <Card className="border-indigo-700/50 bg-indigo-900/40 backdrop-blur" data-testid="other-apks-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-indigo-200 text-base">APK Lainnya</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {otherApks.map(apk => (
                <a
                  key={apk.filename}
                  href={`${API_URL}/api/download/apk/${apk.filename}`}
                  download
                  className="flex items-center justify-between p-3 rounded-lg bg-indigo-800/30 hover:bg-indigo-800/50 transition-colors group"
                  data-testid={`download-${apk.filename}`}
                >
                  <div className="flex items-center gap-3">
                    <FileDown className="w-5 h-5 text-indigo-400" />
                    <div>
                      <p className="text-white text-sm font-medium">{apk.filename}</p>
                      <p className="text-indigo-400 text-xs">{apk.size_mb} MB — {apk.variant}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-indigo-500 group-hover:text-white transition-colors" />
                </a>
              ))}
            </CardContent>
          </Card>
        )}

        {apks.length === 0 && !loading && (
          <Card className="border-red-500/30 bg-indigo-900/40">
            <CardContent className="py-8 text-center">
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-white">Tidak ada APK tersedia saat ini.</p>
            </CardContent>
          </Card>
        )}

        {/* Install Instructions */}
        <Card className="border-indigo-700/50 bg-indigo-900/40 backdrop-blur" data-testid="install-guide">
          <CardHeader className="pb-2">
            <CardTitle className="text-indigo-200 text-base flex items-center gap-2">
              <Info className="w-4 h-4 text-indigo-400" />
              Cara Install di Android
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm text-indigo-300">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center text-xs font-bold">1</span>
                <span>Download file APK dari tombol di atas</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center text-xs font-bold">2</span>
                <span>Buka <strong className="text-white">Pengaturan</strong> &gt; <strong className="text-white">Keamanan</strong> &gt; aktifkan <strong className="text-white">"Sumber tidak dikenal"</strong> atau <strong className="text-white">"Install dari sumber tidak dikenal"</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center text-xs font-bold">3</span>
                <span>Buka file APK yang sudah didownload dan ketuk <strong className="text-white">Install</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center text-xs font-bold">4</span>
                <span>Buka aplikasi SIGAP GURU dan login dengan NIP/NUPTK Anda</span>
              </li>
            </ol>

            <Alert className="mt-4 border-amber-500/30 bg-amber-500/10">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <AlertDescription className="text-amber-200 text-xs">
                Jika muncul peringatan "Play Protect", ketuk <strong>Tetap Install</strong>. Aplikasi ini aman dan telah diverifikasi.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-indigo-300 text-xs pt-4">
          SIGAP GURU {buildVersion ? `v${buildVersion}` : ''} — Dinas Pendidikan Kabupaten Asmat
        </p>
      </div>
    </div>
  );
};

export default DownloadPage;
